/**
 * Authentication Service
 * Handles all authentication-related API calls
 */

import api from './api';
import { AUTH_ENDPOINTS } from './endpoints';

/**
 * Login with ticket number and password
 * @param {Object} credentials - User credentials
 * @param {string} credentials.ticketNumber - Ticket number
 * @param {string} credentials.password - Password
 * @returns {Promise<Object>} Response with token
 */
export const login = async (credentials) => {
  try {
    const response = await api.post(AUTH_ENDPOINTS.LOGIN, credentials);

    console.log('[AuthService] Full response:', {
      status: response.status,
      statusText: response.statusText,
      headers: response.headers,
      data: response.data,
    });

    // Handle 204 No Content - token might be in headers
    if (response.status === 204) {
      console.log('[AuthService] Got 204 response, checking headers for token...');

      // Check if token is in response headers
      const token = response.headers['authorization'] ||
                    response.headers['Authorization'] ||
                    response.headers['x-auth-token'] ||
                    response.headers['X-Auth-Token'];

      if (token) {
        console.log('[AuthService] Found token in headers');
        // Remove "Bearer " prefix if present
        const cleanToken = token.replace(/^Bearer\s+/i, '');
        return { token: cleanToken };
      }

      // If no token in headers, throw error
      console.error('[AuthService] No token found in headers for 204 response');
      throw new Error('No token received from server (204 with no token in headers)');
    }

    // Handle standard response with body (200, etc.)
    console.log('[AuthService] Returning response.data:', response.data);

    // Check if response.data has token
    if (response.data && response.data.token) {
      console.log('[AuthService] Token found in response.data.token');
      return response.data;
    } else if (typeof response.data === 'string') {
      // Token might be a plain string
      console.log('[AuthService] Response data is string, treating as token');
      return { token: response.data };
    } else {
      console.error('[AuthService] No token found in response body:', response.data);
      throw new Error('No token in response body');
    }
  } catch (error) {
    console.error('[AuthService] Login error:', error);
    console.error('[AuthService] Response status:', error.response?.status);
    console.error('[AuthService] Response headers:', error.response?.headers);
    console.error('[AuthService] Response data:', error.response?.data);

    // Re-throw with additional context
    throw {
      status: error.response?.status,
      message: error.response?.data || error.message,
      originalError: error,
    };
  }
};

/**
 * Auto login with ticket number and hash
 * @param {Object} credentials - Auto login credentials
 * @param {string} credentials.ticketNumber - Ticket number
 * @param {string} credentials.hash - Hash for auto login
 * @returns {Promise<Object>} Response with token
 */
export const autoLogin = async (credentials) => {
  try {
    const response = await api.post(AUTH_ENDPOINTS.AUTO_LOGIN, credentials);
    return response.data;
  } catch (error) {
    throw {
      status: error.response?.status,
      message: error.response?.data || error.message,
      originalError: error,
    };
  }
};

/**
 * Check if response indicates successful authentication
 * @param {Object} response - API response
 * @returns {boolean} True if authentication was successful
 */
export const isAuthSuccessful = (response) => {
  return response && response.token;
};
